// S45 A2 — generic retry wrapper for `supabase.functions.invoke()` calls.
// Centralizes exponential backoff (3 attempts × 500ms × 2^i) with error
// classification: retries network/server failures, fails fast on
// 4xx validation/auth/rate-limit so we never duplicate leads.
//
// Replaces ad-hoc invoke() in 8 forms (ContactForm, ExitIntent, EmailCapture,
// Newsletter, Simulatore PDF, PartnerFiduciari, Blog newsletter, Contatto).
// recordConsentWithRetry uses this underneath + adds localStorage replay
// for GeBüV audit trail.

import { supabase } from "./supabase";
import { classifyForRetry } from "./errors";

export interface InvokeOptions {
  attempts?: number;
  baseDelayMs?: number;
  signal?: AbortSignal;
}

export interface InvokeResult<T> {
  data: T | null;
  error: unknown;
  attempts: number;
}

const DEFAULT_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 500;

export async function invokeWithBackoff<TResp = unknown>(
  fnName: string,
  body: unknown,
  opts: InvokeOptions = {},
): Promise<InvokeResult<TResp>> {
  const attempts = opts.attempts ?? DEFAULT_ATTEMPTS;
  const baseDelay = opts.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  let lastErr: unknown = null;

  for (let i = 0; i < attempts; i++) {
    if (opts.signal?.aborted) {
      return { data: null, error: lastErr ?? new Error("aborted"), attempts: i };
    }
    const { data, error } = await supabase.functions.invoke<TResp>(fnName, {
      body: body as Record<string, unknown>,
    });
    if (!error) {
      return { data: (data ?? null) as TResp | null, error: null, attempts: i + 1 };
    }
    lastErr = error;
    if (classifyForRetry(error) === "fail-fast") {
      return { data: null, error: lastErr, attempts: i + 1 };
    }
    if (i < attempts - 1) {
      await new Promise<void>((r) => setTimeout(r, baseDelay * Math.pow(2, i)));
    }
  }
  return { data: null, error: lastErr, attempts };
}
